/** User-guide catalog for DreamHomeJourney.online. Role-visible; no DRE claims. */

function topic(input) {
  return {
    examples:
      input.examples ||
      "Open the matching desk in REChecklist, work one quiet step, then return here if you need the why.",
    placeholder:
      input.placeholder ||
      "A longer walkthrough, still frames, and a short video will land here when the Guided Tour is recorded.",
    roles: input.roles || ["public"],
    ...input,
  };
}

export const GUIDE_TOPICS = [
  topic({
    id: "g-account",
    slug: "account-management",
    letter: "A",
    title: "Account management",
    roles: ["public", "buyer", "seller", "agent", "broker", "admin"],
    purpose: "Keep a calm sign-in so buyer, seller, agent, and broker desks never mix.",
    function:
      "REChecklist holds the account. DreamHomeJourney only receives role and user as query params from Support or Billing links.",
    usage: "Use Login on this site to open the correct REChecklist door. Password reset on the Admin desk is local/prototype only.",
  }),
  topic({
    id: "g-agent-desk",
    slug: "agent-desk",
    letter: "A",
    title: "Agent desk",
    roles: ["agent", "broker", "admin"],
    purpose: "A framed working desk for assigned clients — not a marketplace.",
    function: "Client Manager, File Packet, documents, calendar, client messaging, and office messaging live under /agent.",
    usage: "Stay on Client Messaging for buyers and sellers. Colleague notes belong in Office Messaging.",
  }),
  topic({
    id: "g-bbs",
    slug: "bbs-usage",
    letter: "B",
    title: "BBS usage",
    roles: ["public", "agent", "broker", "admin"],
    purpose: "A quiet board for meetings, training, and market notes.",
    function: "Community lives at /community (also /board). Posts, comments, likes, search, and role visibility are local on this origin.",
    usage: "Unsigned visitors see public posts only. Sign in context arrives from the app footer Support link.",
  }),
  topic({
    id: "g-broker-desk",
    slug: "broker-desk",
    letter: "B",
    title: "Broker desk",
    roles: ["broker", "admin"],
    purpose: "Supervise the office without turning the desk into a sales floor.",
    function: "My Business vs All Agents, retention, office messaging, and broker profile fill the footer contact card.",
    usage: "Client files stay apart from office notes. Retention is a habit, not a scramble.",
  }),
  topic({
    id: "g-calendar",
    slug: "calendar",
    letter: "C",
    title: "Calendar",
    roles: ["public", "buyer", "seller", "agent", "broker", "admin"],
    purpose: "Hold appointments on the desk and community events on this hub.",
    function:
      "Hub events (San Diego County by default) appear on /calendar here and, when relevant, on the REChecklist agent/broker calendar.",
    usage: "Desk appointments are created on REChecklist. Community conventions and workshops are posted from Admin Controls or the Events platform.",
  }),
  topic({
    id: "g-checklists",
    slug: "checklists",
    letter: "C",
    title: "Checklists",
    roles: ["buyer", "seller", "agent", "broker", "admin"],
    purpose: "A decision companion — buyer discovery and seller portfolio never share state.",
    function: "Buyer path: /start through /complete. Seller path: /seller/home through portfolio summary.",
    usage: "Complete a section when it feels settled. There is no race.",
  }),
  topic({
    id: "g-custom-steps",
    slug: "custom-steps",
    letter: "C",
    title: "Custom steps",
    roles: ["agent", "broker", "admin"],
    purpose: "Add office-specific beats to a file without rewriting the buyer or seller journey.",
    function: "Custom steps live on the File Packet for that client. They are not a second checklist product.",
    usage: "Open the client File, add a step, and keep the tone you would want archived later.",
    placeholder: "Field-level how-to and screenshots will expand this page.",
  }),
  topic({
    id: "g-documents",
    slug: "documents-photos",
    letter: "D",
    title: "Documents & photos",
    roles: ["buyer", "seller", "agent", "broker", "admin"],
    purpose: "Keep media with the file, not in a public gallery.",
    function: "Documents & Photos on agent/broker desks; seller photos stay on the seller journey.",
    usage: "Keep documents under 2 MB; photos may be larger and are compressed locally.",
  }),
  topic({
    id: "g-desktop",
    slug: "desktop-usage",
    letter: "D",
    title: "Desktop usage",
    roles: ["public"],
    purpose: "The framed desk is designed for a calm widescreen read.",
    function: "Agent, broker, and admin desks use a contained scroller so the footer stays below the first screen.",
    usage: "Use sidebar zoom if the woodland texture feels close. Return-to-top sits on the frame.",
  }),
  topic({
    id: "g-events",
    slug: "event-platform",
    letter: "E",
    title: "Event platform usage",
    roles: ["public", "agent", "broker", "admin"],
    purpose: "Find local through national gatherings without a listing feed.",
    function: "Events at /events. Categories include organizations, county, state and national conventions, opens, workshops, training, and expos.",
    usage: "Filter by scope and kind. Region default is San Diego County, with Southern California as the next expansion.",
  }),
  topic({
    id: "g-file-packet",
    slug: "file-packet",
    letter: "F",
    title: "File Packet system",
    roles: ["agent", "broker", "admin"],
    purpose: "One searchable file per client: checklist, timeline, messages, calendar, and packet.",
    function: "Assemble lives in REChecklist. This guide does not copy that engine.",
    usage: "Open Files on the desk. Buyer packets and seller packets stay on their own journeys.",
  }),
  topic({
    id: "g-footer",
    slug: "footer-usage",
    letter: "F",
    title: "Footer usage",
    roles: ["public"],
    purpose: "Information & Resources is the quiet office card — not a second website.",
    function: "REChecklist footer Support opens this site with role and user. License fields are recorded by the office, not certified here.",
    usage: "Fill Agent or Broker profile once; the footer reads it automatically.",
  }),
  topic({
    id: "g-guided-tour",
    slug: "guided-tour",
    letter: "G",
    title: "Guided Tour",
    roles: ["agent", "broker", "admin"],
    purpose: "A full walkthrough so the desk stays a workflow, not a classroom.",
    function: "Tour scripts live at /support/tour. Videos are placeholders until recorded.",
    usage: "Jump-ahead stays locked until the first complete viewing.",
  }),
  topic({
    id: "g-home-prep",
    slug: "home-prep",
    letter: "H",
    title: "Home prep (clients)",
    roles: ["buyer", "seller", "public"],
    purpose: "Moving and readiness notes for clients — education, not a vendor marketplace feed.",
    function: "Client vendor categories and the Client hub collect movers, cleaners, and inspectors as placeholders.",
    usage: "Your agent still leads the file. These pages are companions.",
  }),
  topic({
    id: "g-info-resources",
    slug: "information-resources",
    letter: "I",
    title: "Information & Resources",
    roles: ["public"],
    purpose: "The REChecklist footer band for contact, support, legal, and notices.",
    function: "Support is this origin. Notices are system updates, never mixed with Messages.",
    usage: "Use Return-to-top on the frame, then scroll to the band when you need it.",
  }),
  topic({
    id: "g-journey-modes",
    slug: "journey-modes",
    letter: "J",
    title: "Journey modes",
    roles: ["buyer", "seller", "agent", "admin"],
    purpose: "Buyer and seller modes never mix. Separate routes, nav, and storage.",
    function: "DreamHome Buyer’s Checklist vs Seller’s Checklist. Agents leave with something usable from each.",
    usage: "Do not rewrite buyer pages to share with seller. Open the matching door.",
  }),
  topic({
    id: "g-key-dates",
    slug: "key-dates",
    letter: "K",
    title: "Key dates",
    roles: ["buyer", "seller", "agent", "broker"],
    purpose: "Escrow and showing dates belong on the file calendar, not a public scoreboard.",
    function: "Desk calendar + hub events. Placeholder for a printed key-dates card.",
    placeholder: "A sample key-dates template will be added with broker review.",
  }),
  topic({
    id: "g-lifecycle",
    slug: "lifecycle-tracker",
    letter: "L",
    title: "Lifecycle tracker",
    roles: ["agent", "broker", "admin"],
    purpose: "See where a file sits without turning it into a pipeline race.",
    function: "Lifecycle lives on the File Packet. Close and abandon archive the conversation.",
    usage: "Treat status changes as archive-worthy notes.",
  }),
  topic({
    id: "g-messaging",
    slug: "messaging",
    letter: "M",
    title: "Messaging",
    roles: ["buyer", "seller", "agent", "broker", "admin"],
    purpose: "A quiet thread with the file — not a second inbox product.",
    function: "Client Messaging vs Office Messaging share one store and stay visually apart. Admin does not fork the inbox.",
    usage: "Write as if a broker may read it later.",
  }),
  topic({
    id: "g-mobile",
    slug: "mobile-usage",
    letter: "M",
    title: "Mobile usage",
    roles: ["public"],
    purpose: "The same woodland read on a smaller frame.",
    function: "Drawers replace sidebars. Pure CSS; no PWA unless asked.",
    usage: "Add to Home Screen is instructional only in this prototype.",
  }),
  topic({
    id: "g-navigation",
    slug: "navigation",
    letter: "N",
    title: "Navigation",
    roles: ["public"],
    purpose: "This site is sales, support, and community. The app is the desk.",
    function: "Header: Home, User Guide, Vendors, Events, Support, Community, Contact, Login.",
    usage: "Role and user query params copy across header and footer when you arrived from REChecklist Support.",
  }),
  topic({
    id: "g-office-msg",
    slug: "office-messaging",
    letter: "O",
    title: "Office messaging",
    roles: ["agent", "broker", "admin"],
    purpose: "Colleague notes without mixing client files.",
    function: "/agent/office and /broker/office. Inquiries use the same messaging store.",
    usage: "Start with Inquiry on the office desk. Client threads stay next door.",
  }),
  topic({
    id: "g-privacy",
    slug: "privacy-security",
    letter: "P",
    title: "Privacy & security",
    roles: ["public"],
    purpose: "Local-first prototype data. No claim of legal certification.",
    function: "System notices for security updates. Archives are retention-aligned, not public.",
    usage: "Read Notices on REChecklist. Counsel-reviewed terms come before production.",
  }),
  topic({
    id: "g-quiet-desk",
    slug: "quiet-desk",
    letter: "Q",
    title: "Quiet desk (tone)",
    roles: ["public"],
    purpose: "A magic carpet ride, not a race or a boxing ring.",
    function: "Copy, motion, and layout stay woodland academic — HomeSpec precision with DreamHome warmth.",
    usage: "If a page feels loud, it is a bug. Prefer fewer cards, fuller sentences.",
  }),
  topic({
    id: "g-reports",
    slug: "reports",
    letter: "R",
    title: "Reports",
    roles: ["agent", "broker", "admin"],
    purpose: "Communication snapshots for the archive — not marketing packets.",
    function: "HTML + PDF export on close. Sample reports live on the Admin desk.",
    usage: "Broker and agent are always flagged for email; client only if opted in. Mail is local/prototype.",
  }),
  topic({
    id: "g-settings",
    slug: "settings",
    letter: "S",
    title: "Settings",
    roles: ["buyer", "seller", "agent", "broker"],
    purpose: "Profile, office identity, and suggestion notes.",
    function: "Agent settings fill the footer. Suggestions from every desk reach Admin Suggestions.",
    usage: "Record license numbers you were issued. This product does not grant or verify them.",
  }),
  topic({
    id: "g-transaction",
    slug: "transaction-checklist",
    letter: "T",
    title: "Transaction checklist",
    roles: ["buyer", "agent", "admin"],
    purpose: "Closing beats as a companion, not a legal form set.",
    function: "Buyer transaction panel and file lifecycle. Educational escrow copy lives on this hub.",
    usage: "Your escrow officer still leads the file.",
  }),
  topic({
    id: "g-uploads",
    slug: "upload-limits",
    letter: "U",
    title: "Upload limits",
    roles: ["buyer", "seller", "agent", "broker"],
    purpose: "Keep the local prototype light.",
    function: "Documents under 2 MB; photos compressed, up to 5 MB before compress.",
    usage: "If a save fails, try one file at a time.",
  }),
  topic({
    id: "g-vendors",
    slug: "vendor-directory",
    letter: "V",
    title: "Vendor directory usage",
    roles: ["public", "buyer", "seller", "agent", "broker"],
    purpose: "Two directories: real-estate pros and client support. Not a listing marketplace.",
    function: "/vendors, /vendors/pro, /vendors/client, and vendor detail pages. Admin CRUD on REChecklist Controls.",
    usage: "Search and filter by category. Placeholders stand until offices add their own.",
  }),
  topic({
    id: "g-woodland",
    slug: "woodland-desks",
    letter: "W",
    title: "Woodland desks (visual language)",
    roles: ["public"],
    purpose: "Cinematic sandstone, evergreen, and sky — professional without being cold.",
    function: "Pure CSS. This marketing site and REChecklist share the same tokens, not the same pages.",
    usage: "Prefer full frames over empty card grids.",
  }),
  topic({
    id: "g-exports",
    slug: "exports-archive",
    letter: "X",
    title: "Exports & archive",
    roles: ["agent", "broker", "admin"],
    purpose: "Close or abandon once; keep the snapshot.",
    function: "Idempotent export on REChecklist. This site does not duplicate archive storage.",
    usage: "Use Admin Messages only as supervision context, not a second mailbox.",
  }),
  topic({
    id: "g-your-notes",
    slug: "your-notes",
    letter: "Y",
    title: "Your notes",
    roles: ["buyer", "seller"],
    purpose: "Sidebar notes stay on your journey.",
    function: "Buyer and seller companion sidebars. They do not leak across modes.",
    usage: "Write what you know. You are not drafting a disclosure form here.",
  }),
  topic({
    id: "g-zoom",
    slug: "zoom-frames",
    letter: "Z",
    title: "Zoom & frames",
    roles: ["public", "agent", "broker", "admin"],
    purpose: "Contain the desk so the first screen stays calm.",
    function: "Frame zoom on REChecklist desks and journey sidebars.",
    usage: "If a control feels clipped, use Return-to-top on that frame, not the browser chrome.",
  }),
];

export function guidesByLetter() {
  const map = {};
  for (let i = 0; i < 26; i += 1) {
    map[String.fromCharCode(65 + i)] = [];
  }
  GUIDE_TOPICS.forEach((item) => {
    const letter = String(item.letter || "A").toUpperCase();
    if (!map[letter]) map[letter] = [];
    map[letter].push(item);
  });
  return map;
}

export function canSeeGuide(topic, role) {
  const roles = topic.roles || ["public"];
  if (roles.includes("public")) return true;
  if (role === "admin") return true;
  return Boolean(role && roles.includes(role));
}

export function getGuideBySlug(slug) {
  return GUIDE_TOPICS.find((item) => item.slug === slug) || null;
}

export function listGuides(role) {
  return GUIDE_TOPICS.filter((item) => canSeeGuide(item, role));
}
